import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface UseAssistantUrlChatSelectionProps {
  queryParam: string
}

interface LocationParts {
  hash: string
  pathname: string
  search: string
}

interface ChatSelection {
  chatId: number | undefined
  shouldClearUrl: boolean
}

function parseChatId(rawChatId: string | null): number | undefined {
  if (!rawChatId) {
    return undefined
  }

  const chatId = Number(rawChatId)
  return Number.isSafeInteger(chatId) && chatId > 0 ? chatId : undefined
}

function buildLocationWithChatId(queryParam: string, chatId: number | undefined, location: LocationParts): LocationParts {
  const searchParams = new URLSearchParams(location.search)
  if (chatId === undefined) {
    searchParams.delete(queryParam)
  } else {
    searchParams.set(queryParam, String(chatId))
  }

  const nextSearch = searchParams.toString()
  return {
    hash: location.hash,
    pathname: location.pathname,
    search: nextSearch ? `?${nextSearch}` : '',
  }
}

function readChatSelection(queryParam: string, search: string): ChatSelection {
  const searchParams = new URLSearchParams(search)
  if (!searchParams.has(queryParam)) {
    return {
      chatId: undefined,
      shouldClearUrl: false,
    }
  }

  const chatId = parseChatId(searchParams.get(queryParam))
  return {
    chatId,
    shouldClearUrl: chatId === undefined,
  }
}

export function useAssistantUrlChatSelection({ queryParam }: UseAssistantUrlChatSelectionProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const currentSelection = useMemo(() => readChatSelection(queryParam, location.search), [location.search, queryParam])
  const previousPathname = useRef(location.pathname)
  const [selectedChatId, setSelectedChatId] = useState(currentSelection.chatId)

  const replaceCurrentUrlChatId = useCallback((chatId: number | undefined) => {
    const nextLocation = buildLocationWithChatId(queryParam, chatId, location)
    if (
      nextLocation.hash !== location.hash ||
      nextLocation.pathname !== location.pathname ||
      nextLocation.search !== location.search
    ) {
      navigate(nextLocation, { replace: true, state: location.state })
    }
  }, [location, navigate, queryParam])

  useEffect(() => {
    const pathnameChanged = previousPathname.current !== location.pathname
    previousPathname.current = location.pathname

    if (currentSelection.shouldClearUrl) {
      replaceCurrentUrlChatId(undefined)
      setSelectedChatId(undefined)
      return
    }

    if (currentSelection.chatId === undefined && !pathnameChanged) {
      return
    }

    setSelectedChatId(previous => previous === currentSelection.chatId ? previous : currentSelection.chatId)
  }, [currentSelection, location.pathname, replaceCurrentUrlChatId])

  const selectChat = useCallback((chatId: number) => {
    setSelectedChatId(chatId)
    replaceCurrentUrlChatId(chatId)
  }, [replaceCurrentUrlChatId])

  const clearChatSelection = useCallback(() => {
    setSelectedChatId(undefined)
    replaceCurrentUrlChatId(undefined)
  }, [replaceCurrentUrlChatId])

  const removeChatIdFromUrl = useCallback(() => {
    if (!new URLSearchParams(location.search).has(queryParam)) {
      return
    }

    replaceCurrentUrlChatId(undefined)
  }, [location.search, queryParam, replaceCurrentUrlChatId])

  return {
    selectedChatId,
    urlChatId: currentSelection.chatId,
    selectChat,
    clearChatSelection,
    removeChatIdFromUrl,
  }
}
